import { 
  TreasuryAccountSchema, 
  CreateTreasuryAccountRequest, 
  UpdateTreasuryAccountRequest, 
  TreasuryAccountQueryParams 
} from '../types/treasury-accounts';
import { getAuthToken } from '../utils/auth';

const API_BASE_URL = import.meta.env.VITE_APP_API_URL;

const TREASURY_ACCOUNTS_ENDPOINT = `${API_BASE_URL}/admin/treasury-accounts`;

export const createTreasuryAccounts = async (data: CreateTreasuryAccountRequest): Promise<TreasuryAccountSchema[]> => {
  const response = await fetch(TREASURY_ACCOUNTS_ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `ClientBearer ${getAuthToken()}`,
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    throw new Error(`Failed to create treasury accounts: ${response.statusText}`);
  }

  return response.json();
};

export const getTreasuryAccounts = async (params?: TreasuryAccountQueryParams): Promise<{
  accounts: TreasuryAccountSchema[];
  next_cursor?: string;
}> => {
  const queryParams = new URLSearchParams();
  
  if (params?.currency) queryParams.append('currency', params.currency);
  if (params?.direction) queryParams.append('direction', params.direction);
  if (params?.include_all) queryParams.append('include_all', 'true');
  if (params?.next_cursor) queryParams.append('next_cursor', params.next_cursor);

  const url = `${TREASURY_ACCOUNTS_ENDPOINT}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
  
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Authorization': `ClientBearer ${getAuthToken()}`,
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch treasury accounts: ${response.statusText}`);
  }

  const data = await response.json();
  
  // Transform the response to match expected structure
  return {
    accounts: data.treasury_accounts || [],
    next_cursor: data.next_cursor
  };
};

export const getTreasuryAccount = async (id: string): Promise<TreasuryAccountSchema> => {
  const response = await fetch(`${TREASURY_ACCOUNTS_ENDPOINT}/${id}`, {
    method: 'GET',
    headers: {
      'Authorization': `ClientBearer ${getAuthToken()}`,
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch treasury account: ${response.statusText}`);
  }

  return response.json();
};

export const updateTreasuryAccount = async (id: string, data: UpdateTreasuryAccountRequest): Promise<TreasuryAccountSchema> => {
  const response = await fetch(`${TREASURY_ACCOUNTS_ENDPOINT}/${id}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `ClientBearer ${getAuthToken()}`,
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    throw new Error(`Failed to update treasury account: ${response.statusText}`);
  }

  return response.json();
};

export const deleteTreasuryAccount = async (id: string): Promise<void> => {
  const response = await fetch(`${TREASURY_ACCOUNTS_ENDPOINT}/${id}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `ClientBearer ${getAuthToken()}`,
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to delete treasury account: ${response.statusText}`);
  }
};
